import { useState } from "react";
import { CircleCheck, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  GEOCODE_MINIMUM,
  useGeocodeSuggesties,
  type GeocodeTreffer,
} from "@/features/reistijden/useReistijden";
import { useDebounced } from "@/lib/debounce";
import { cn } from "@jbnl/shared-ui";
import type { Meldadres } from "./useWedstrijden";

/** Long enough that a fast typist sends one query per word, short enough not to feel laggy. */
const WACHTTIJD = 300;

/** The chosen address as one line, which is what the box shows when nothing is being typed. */
function adresRegel(meldadres: Meldadres) {
  const plaats = [meldadres.postalCode, meldadres.city]
    .filter(Boolean)
    .join(" ")
    .trim();

  return [meldadres.address, plaats].filter(Boolean).join(", ").trim();
}

// Only once the answer is in *for what is currently typed*. Without the first two checks an
// empty list reads as "not found" while the very first request is still in flight, and again
// during the debounce window before it is sent.
function berekenNietsGevonden(
  isFetching: boolean,
  gezocht: string,
  zoekterm: string,
  aantalSuggesties: number,
) {
  return (
    !isFetching &&
    gezocht.trim() === zoekterm.trim() &&
    zoekterm.trim().length >= GEOCODE_MINIMUM &&
    aantalSuggesties === 0
  );
}

/** The empty-state text is worth showing only while the box is open and nothing else is wrong. */
function toonNietsGevondenMelding(
  open: boolean,
  heeftFout: boolean,
  nietsGevonden: boolean,
) {
  return open && !heeftFout && nietsGevonden;
}

/** Where the highlight lands after an arrow key, clamped to the ends of the list. */
function volgendeActief(
  richting: "ArrowDown" | "ArrowUp",
  huidig: number,
  aantal: number,
) {
  return richting === "ArrowDown"
    ? Math.min(huidig + 1, aantal - 1)
    : Math.max(huidig - 1, 0);
}

/**
 * Four states, and only the last two are unfinished: ADR-0013 routes a dag without a point
 * from the organiserend korps, so a blanket warning would be false (ADR-0060).
 */
function MeldadresStatus({
  meldadres,
  korpsNaam,
  korpsAdres,
  handmatig,
}: {
  meldadres: Meldadres;
  /** The organiserend korps, which is where a meldadres without a point is routed from. */
  korpsNaam: string;
  /** Whether this meldadres names the korps's own kazerne, and so overrides nothing. */
  korpsAdres: boolean;
  /** Whether the address text was typed into the fields rather than chosen or taken over. */
  handmatig: boolean;
}) {
  const { t } = useTranslation("wedstrijden");

  if (meldadres.latitude != null && meldadres.longitude != null) {
    // Plain text, not a notice at all: nothing is wrong and nothing is asked. A confirmation
    // that shouts is a confirmation nobody reads the next time it matters.
    return (
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <CircleCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
        <span>{t("meldadresZoeker.bevestigd")}</span>
      </p>
    );
  }

  if (korpsAdres) {
    // Settled, and shown the same way. This dag is held at its own korps's kazerne and
    // routes from Portaal's point for it - there is nothing here left to look up, so a
    // notice asking for attention would be asking for it on behalf of nobody.
    return (
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <CircleCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
        <span>{t("meldadresZoeker.korpsAdres")}</span>
      </p>
    );
  }

  if (handmatig) {
    // Typed, and it names somewhere the reistijd will not be measured from.
    return (
      <Alert tone="warning">
        {t("meldadresZoeker.nietGekozen", { korps: korpsNaam })}
      </Alert>
    );
  }

  // A stored override carrying no point - not this korps's kazerne, and not typed here.
  return <Alert>{t("meldadresZoeker.zonderPunt", { korps: korpsNaam })}</Alert>;
}

/**
 * Choosing a suggestion is the confirmation ADR-0013 asks for, since a wrong coordinate fails
 * silently. Nothing commits on its own: auto-taking a briefly-unique candidate would store a point
 * mid-word (docs/adr/0060-a-meldadres-is-chosen-not-typed.md).
 */
export function MeldadresZoeker({
  id,
  meldadres,
  korpsNaam,
  korpsAdres,
  handmatig,
  onKies,
}: {
  id: string;
  meldadres: Meldadres;
  /** The organiserend korps, which is where a meldadres without a point is routed from. */
  korpsNaam: string;
  /** Whether this meldadres names the korps's own kazerne, and so overrides nothing. */
  korpsAdres: boolean;
  /** Whether the address text was typed into the fields rather than chosen or taken over. */
  handmatig: boolean;
  onKies: (treffer: GeocodeTreffer) => void;
}) {
  const { t } = useTranslation("wedstrijden");

  const gekozen = adresRegel(meldadres);

  const [zoekterm, setZoekterm] = useState(gekozen);
  const [open, setOpen] = useState(false);
  const [actief, setActief] = useState(0);

  // The address can change from outside this box - a korps being picked takes its address over,
  // and the fields below stay editable - so the box follows it rather than holding a stale copy.
  const [vorigGekozen, setVorigGekozen] = useState(gekozen);

  if (gekozen !== vorigGekozen) {
    setVorigGekozen(gekozen);
    setZoekterm(gekozen);
    setOpen(false);
  }

  const gezocht = useDebounced(zoekterm, WACHTTIJD);
  const {
    data: suggesties = [],
    error,
    isFetching,
  } = useGeocodeSuggesties(gezocht);
  const nietsGevonden = berekenNietsGevonden(
    isFetching,
    gezocht,
    zoekterm,
    suggesties.length,
  );

  const lijstId = `${id}-suggesties`;
  const optieId = (index: number) => `${id}-suggestie-${index}`;
  const zichtbaar = open && suggesties.length > 0;

  function kies(treffer: GeocodeTreffer) {
    onKies(treffer);
    setOpen(false);
  }

  function typ(waarde: string) {
    setZoekterm(waarde);
    setOpen(true);
    // Back to the top hit: the old highlight belonged to a list that no longer exists, and
    // leaving it where it was is how Enter takes an address nobody was looking at.
    setActief(0);
  }

  // Once the list is open, the remaining keys drive it rather than the page - split out so
  // "should the list react at all" and "how it reacts" don't pile up into one function.
  function verwerkLijstToets(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      const richting = event.key;

      event.preventDefault();
      setActief((huidig) =>
        volgendeActief(richting, huidig, suggesties.length),
      );
      return;
    }

    if (event.key === "Enter") {
      // Without this the form submits: this is the only Enter on the screen that means
      // "take the highlighted address" rather than "save the wedstrijddag".
      event.preventDefault();
      kies(suggesties[actief]);
    }
  }

  function toets(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }

    if (event.key === "ArrowDown" && !zichtbaar) {
      setOpen(true);
      return;
    }

    if (zichtbaar) {
      verwerkLijstToets(event);
    }
  }

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{t("meldadresZoeker.label")}</Label>

      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id={id}
          className="pl-9"
          role="combobox"
          aria-expanded={zichtbaar}
          aria-controls={lijstId}
          aria-autocomplete="list"
          aria-activedescendant={zichtbaar ? optieId(actief) : undefined}
          autoComplete="off"
          placeholder={t("meldadresZoeker.placeholder")}
          value={zoekterm}
          onChange={(event) => typ(event.target.value)}
          onKeyDown={toets}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        />

        {zichtbaar && (
          <ul
            id={lijstId}
            role="listbox"
            aria-label={t("meldadresZoeker.label")}
            className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md"
          >
            {suggesties.map((treffer, index) => (
              <li
                key={`${treffer.latitude},${treffer.longitude}`}
                id={optieId(index)}
                role="option"
                aria-selected={index === actief}
                className={cn(
                  "cursor-pointer px-3 py-2 text-sm",
                  index === actief && "bg-accent text-accent-foreground",
                )}
                // Not onClick alone: a click blurs the input first, which closes the list out
                // from under the pointer. Holding focus here is what lets the click land.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActief(index)}
                onClick={() => kies(treffer)}
              >
                {treffer.omschrijving}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error.message}</p>}

      {toonNietsGevondenMelding(open, error != null, nietsGevonden) && (
        <p className="text-sm text-muted-foreground">
          {t("meldadresZoeker.nietGevonden")}
        </p>
      )}

      <MeldadresStatus
        meldadres={meldadres}
        korpsNaam={korpsNaam}
        korpsAdres={korpsAdres}
        handmatig={handmatig}
      />
    </div>
  );
}
